// src/api/userPrefs.api.ts — SCE Issues
// CRUD des préférences utilisateur dans SharePoint SCE_UserPrefs
//
// ─── Schéma de la liste ───
//
//   Title  → UPN de l'utilisateur (clé unique, 1 ligne par user)
//   Prefs  → JSON sérialisé (texte multiligne, plain text)
//
// La version du schéma est embarquée DANS le JSON (champ `version`) :
// pas de colonne dédiée → plus simple à faire évoluer.
//
// ─── Fonctionnement ───
//
// 1. `getMyPrefs(upn)` lit la ligne dont Title = upn (case-insensitive).
//    Renvoie null si l'user n'a pas encore enregistré ses prefs.
//
// 2. `saveMyPrefs(upn, prefs)` fait un POST si l'user n'a pas de ligne,
//    sinon un PATCH sur la ligne existante. C'est un "upsert".
//
// L'appelant (useDashboardPrefs) est responsable du debounce côté UI.

import { graphGet, graphPost, graphPatch, GraphApiError } from './graphClient'
import {
  CRITICITES_ORDRE,
  FILTRES_BADGE,
  PHASES_ORDRE,
  STATUTS_ORDRE,
  type Criticite,
  type FiltreBadgeCloture,
  type Phase,
  type Statut,
} from '@/types/issue.types'
import { TYPE_PAR_CATEGORIE, TYPES_EQUIPEMENT } from '@/config/equipements.catalogue'
import { NON_IDENTIFIE, type SerieCouleur } from '@/utils/dashboardAggregations'
import { METIERS_ORDRE } from '@/config/metiers.config'
import { rangeeMetiersEtSujets, type ClePerimetre } from '@/config/sujets.config'
import { SUJETS } from '@/types/issue.types'
import {
  ecrireSuivis,
  lireSuivis,
  LONGUEUR_MAX_SUIVIS,
  suit,
  suivisApres,
  type ElementsSuivis,
  type GenreSuivi,
} from '@/utils/elementsSuivis'
import { nomComplet } from '@/utils/noms'
import { nomDansLaTable } from '@/config/comptesAlias.config'
import { reglagesDepuisLigne, type ReglagesNotification } from '@/utils/notificationsFnct'
import type { ReglagesMentionne } from '@/utils/mentions'
import { CLE_RETOUR_ABSENCE, lireBlocRetour, noterRetour, type BlocRetourAbsence } from '@/utils/retourAbsence'

// ─── Configuration SharePoint ───

const SITE_ID = import.meta.env.VITE_SHAREPOINT_SITE_ID ?? ''
const LIST_ID = import.meta.env.VITE_LIST_USERPREFS_ID ?? ''

function listPath(): string {
  return `/sites/${SITE_ID}/lists/${LIST_ID}`
}

// ─── Schéma des prefs ───

/**
 * Version courante du schéma. Bump à chaque breaking change.
 * Un JSON de version inférieure est migré côté client (`migratePrefs`).
 *
 * V2 (Lot 5) : filtres par type d'équipement, catégorie, modèle, PN, phase,
 * badge de clôture, et le choix « Colorer par » du graphique principal.
 * V3 (24/09) : « Mes déclarations uniquement » n'est plus coché par défaut.
 * La migration le décoche UNE fois pour les réglages V1/V2, où il l'était
 * d'office pour les opérateurs, pas par choix.
 */
export const PREFS_SCHEMA_VERSION = 3

export type PeriodePrefs = 'week' | 'month' | 'quarter' | 'all'

export interface DashboardPrefs {
  version: 3
  periode: PeriodePrefs
  /** Métiers, et sujets (23/10 : GOULOTTE) — un problème de goulotte répond aux deux. */
  metiers: ClePerimetre[]
  statuts: Statut[]
  criticites: Criticite[]
  mesDeclarationsUniquement: boolean
  mesMetiersCoordUniquement: boolean
  chefProjet: string | null
  // ── V2 ──
  /**
   * Axe équipement. Le graphique principal DESCEND le long de ces trois
   * listes : cliquer une barre y place une seule valeur. La sélection du
   * graphique est donc un filtre comme un autre — sauvegardé, exporté.
   */
  /** Types, catégories et modèles — ou NON_IDENTIFIE pour chacun. */
  types: string[]
  categories: string[]
  /** Libellés de modèle (SCE_Equipements.field_3). */
  modeles: string[]
  /** PN, graphie d'affichage. Comparé via cleRapprochement. */
  pn: string | null
  phases: Phase[]
  badges: FiltreBadgeCloture[]
  colorerPar: SerieCouleur
}

// ─── Valeurs par défaut ───
//
// Note : `mesMetiersCoordUniquement` est mis à true par défaut pour les
// coordinateurs (cf. buildDefaultPrefs), mais ici on garde une valeur
// neutre — c'est l'appelant qui ajuste selon le rôle.

export const PREFS_DEFAUT: DashboardPrefs = {
  version: 3,
  periode: 'month',
  metiers: [],
  statuts: [],
  criticites: [],
  mesDeclarationsUniquement: false,
  mesMetiersCoordUniquement: false,
  chefProjet: null,
  types: [],
  categories: [],
  modeles: [],
  pn: null,
  phases: [],
  badges: [],
  colorerPar: 'metier',
}

// ─── Structure brute Graph API ───

interface UserPrefsFields {
  Title?: string
  Prefs?: string
}

// Présence : colonnes créées le 24/09, sans espace ni accent.
const COL_ACTIVITE = 'DerniereActivite' // Date et heure
const COL_PAGE = 'PageActuelle'         // Une ligne de texte

interface SharePointListItem<T> {
  id: string
  fields: T
}

interface SharePointListResponse<T> {
  value: SharePointListItem<T>[]
}

// ─── Sérialisation / parsing ───

function serializePrefs(prefs: DashboardPrefs): string {
  return JSON.stringify(prefs)
}

/**
 * Parse + migration. Si le JSON est corrompu ou que la version est inconnue,
 * on retourne les prefs par défaut (fallback safe — l'user reverra ses
 * vraies prefs au prochain save).
 */
function parsePrefs(raw: string): DashboardPrefs {
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return { ...PREFS_DEFAUT }
    return migratePrefs(parsed as Record<string, unknown>)
  } catch {
    // eslint-disable-next-line no-console
    console.warn('[userPrefs] JSON corrompu — fallback sur défauts')
    return { ...PREFS_DEFAUT }
  }
}

/**
 * Migration vers la version courante.
 *
 * Toute version connue est ramenée d'un coup à la version courante :
 *  - V1 → V2 : les champs communs sont repris tels quels, les nouveaux
 *    prennent leur valeur neutre ;
 *  - V1/V2 → V3 : « Mes déclarations uniquement » est décoché une fois (il
 *    l'était d'office pour les opérateurs, pas par choix). En V3, il est lu.
 * Chaque liste est filtrée sur les valeurs connues : un JSON écrit par une
 * ancienne version (ou retouché à la main) ne doit pas injecter une valeur
 * que plus aucun filtre ne sait afficher.
 */
function migratePrefs(raw: Record<string, unknown>): DashboardPrefs {
  const version = typeof raw['version'] === 'number' ? raw['version'] : 1
  if (version !== 1 && version !== 2 && version !== 3) return { ...PREFS_DEFAUT }

  const chaines = (cle: string): string[] =>
    Array.isArray(raw[cle]) ? (raw[cle] as unknown[]).filter((v): v is string => typeof v === 'string') : []
  function parmi<T extends string>(cle: string, connus: readonly T[]): T[] {
    return chaines(cle).filter((v): v is T => (connus as readonly string[]).includes(v))
  }
  const periode = raw['periode']
  const colorerPar = raw['colorerPar']
  const pn = raw['pn']

  return {
    version: 3,
    periode: isPeriode(periode) ? periode : PREFS_DEFAUT.periode,
    metiers: parmi('metiers', rangeeMetiersEtSujets(METIERS_ORDRE, SUJETS)),
    statuts: parmi('statuts', STATUTS_ORDRE),
    criticites: parmi('criticites', CRITICITES_ORDRE),
    mesDeclarationsUniquement: version === 3 && raw['mesDeclarationsUniquement'] === true,
    mesMetiersCoordUniquement: raw['mesMetiersCoordUniquement'] === true,
    // Enregistré brut avant le 26/09 (« marc.laurent ») : remis au format
    // affiché (« Marc Laurent »), sinon le filtre ne trouverait plus personne.
    chefProjet: typeof raw['chefProjet'] === 'string' && raw['chefProjet'].trim() ? nomComplet(raw['chefProjet']) : null,
    types: parmi('types', [...TYPES_EQUIPEMENT, NON_IDENTIFIE]),
    categories: parmi('categories', [...Object.keys(TYPE_PAR_CATEGORIE), NON_IDENTIFIE]),
    modeles: chaines('modeles'),
    pn: typeof pn === 'string' && pn.trim() !== '' ? pn : null,
    phases: parmi('phases', PHASES_ORDRE),
    badges: parmi('badges', FILTRES_BADGE),
    colorerPar: colorerPar === 'criticite' || colorerPar === 'statut' ? colorerPar : 'metier',
  }
}


function isPeriode(v: unknown): v is PeriodePrefs {
  return v === 'week' || v === 'month' || v === 'quarter' || v === 'all'
}

function escapeODataString(value: string): string {
  return value.replace(/'/g, "''")
}

// ─── Lecture ───

/**
 * Récupère la ligne de l'utilisateur dans SCE_UserPrefs.
 * Retourne null si l'user n'a pas encore enregistré ses prefs.
 *
 * Comparaison Title insensible à la casse — Azure AD ne garantit pas la
 * casse stable de l'UPN.
 */
async function findItemByUpn(
  upn: string,
): Promise<{ itemId: string; prefs: DashboardPrefs | null; brut: Record<string, unknown> } | null> {
  if (!upn) return null
  const filter = `fields/Title eq '${escapeODataString(upn)}'`
  const path =
    `${listPath()}/items?$expand=fields($select=Title,Prefs)` +
    `&$filter=${encodeURIComponent(filter)}&$top=1`

  const response = await graphGet<SharePointListResponse<UserPrefsFields>>(path)
  const item = response.value[0]
  if (!item) return null

  // Une ligne peut exister SANS préférences : la présence la crée pour qui
  // n'a jamais ouvert le dashboard. Pas de prefs = null, pour que l'appelant
  // applique les défauts de SON rôle (et non les défauts neutres).
  const raw = item.fields.Prefs ?? ''
  const brut = lireJsonBrut(raw)
  return {
    itemId: item.id,
    // Une ligne qui ne porte QUE des blocs annexes (accueil, tableau des
    // problèmes) n'a pas de préférences de tableau de bord : null, pour les
    // défauts du rôle.
    prefs: Object.keys(brut).some((k) => k in PREFS_DEFAUT) ? parsePrefs(raw) : null,
    brut,
  }
}

/** Le JSON de la ligne tel quel — {} s'il est vide ou illisible. */
function lireJsonBrut(raw: string): Record<string, unknown> {
  try {
    const v: unknown = JSON.parse(raw)
    return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {}
  } catch {
    return {}
  }
}

/**
 * API publique : récupère les prefs d'un utilisateur ou null.
 */
export async function getMyPrefs(upn: string): Promise<DashboardPrefs | null> {
  const found = await findItemByUpn(upn)
  return found ? found.prefs : null
}

// ─── Écriture (upsert) ───

/**
 * Crée ou met à jour les prefs d'un utilisateur.
 *
 * Stratégie :
 *  1. On cherche la ligne existante.
 *  2. Si présente → PATCH du seul champ Prefs.
 *  3. Sinon → POST avec Title=upn + Prefs.
 *
 * En cas de race condition (deux saves simultanés), le dernier gagne — c'est
 * acceptable pour des prefs UI.
 */
export async function saveMyPrefs(
  upn: string,
  prefs: DashboardPrefs,
): Promise<void> {
  if (!upn) throw new GraphApiError('UPN manquant pour saveMyPrefs', 400)
  const existing = await findItemByUpn(upn)

  if (existing) {
    // Les blocs annexes (accueil : dernière visite ; listeProblemes : réglages du
    // tableau) sont écrits par d'autres pages : on les reprend de la ligne
    // RELUE, jamais de l'état du tableau de bord, qui ne les connaît pas et les
    // effacerait.
    await graphPatch<unknown>(
      `${listPath()}/items/${existing.itemId}/fields`,
      { Prefs: JSON.stringify({ ...blocsAnnexes(existing.brut), ...prefs }) },
    )
    return
  }
  const serialized = serializePrefs(prefs)

  await graphPost<unknown>(`${listPath()}/items`, {
    fields: {
      Title: upn,
      Prefs: serialized,
    },
  })
}

/** Tout ce que la ligne porte en dehors des préférences du tableau de bord. */
function blocsAnnexes(brut: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(brut).filter(([k]) => !(k in PREFS_DEFAUT)))
}

// ─── Blocs annexes du JSON Prefs ───
//
// Une même ligne, plusieurs écrans : chacun a SON bloc (clé) dans le JSON, et
// ne touche jamais aux autres. Lecture et écriture relisent la ligne.

/** Le bloc `cle` de la ligne de l'utilisateur, tel quel ; undefined s'il n'existe pas. */
export async function lireBlocPrefs(upn: string, cle: string): Promise<unknown> {
  if (!upn) return undefined
  const ligne = await findItemByUpn(upn)
  return ligne?.brut[cle]
}

/** Écrit le bloc `cle` sans toucher au reste de la ligne (créée au besoin). */
export async function ecrireBlocPrefs(upn: string, cle: string, valeur: unknown): Promise<void> {
  if (!upn) throw new GraphApiError('UPN manquant pour ecrireBlocPrefs', 400)
  const existant = await findItemByUpn(upn)
  if (existant) {
    await graphPatch<unknown>(`${listPath()}/items/${existant.itemId}/fields`, {
      Prefs: JSON.stringify({ ...existant.brut, [cle]: valeur }),
    })
    return
  }
  await graphPost<unknown>(`${listPath()}/items`, {
    fields: { Title: upn, Prefs: JSON.stringify({ [cle]: valeur }) },
  })
}

// ─── Accueil : dernière visite (Lot 9) ───
//
// « Nouveau depuis votre dernière visite » : la date est retenue dans la ligne
// de l'utilisateur (arbitrage du 25/09 : elle le suit d'un poste à l'autre,
// ce que le navigateur ne ferait pas sur les postes partagés de l'atelier).
// Bloc à part dans le même JSON, que saveMyPrefs préserve.

const CLE_ACCUEIL = 'accueil'

export interface PassageAccueil {
  /** Visite PRÉCÉDENTE (ISO), ou null au premier passage. */
  precedente: string | null
  /** Retour d'absence en cours (28/10, utils/retourAbsence), ou null. */
  retour: BlocRetourAbsence | null
}

/**
 * Lit la date de la visite précédente de l'accueil, puis enregistre celle-ci.
 *
 * Retour d'absence (28/10) : noté dans la MÊME écriture (bloc retourAbsence),
 * au passage qui le découvre ; les suivants le gardent tel quel, la visite
 * précédente pouvant avancer sans lui faire perdre le début de l'absence.
 *
 * @param ecrire     false en bascule de rôle : on lit la personne affichée, on
 *                   n'écrit jamais dans sa ligne (règle du Lot 7).
 * @param avecRetour false pour un fournisseur : pas de récap.
 */
export async function passerSurAccueil(
  upn: string,
  maintenant: string,
  ecrire: boolean,
  avecRetour = true,
): Promise<PassageAccueil> {
  if (!upn) return { precedente: null, retour: null }
  const existant = await findItemByUpn(upn)
  const bloc = existant?.brut[CLE_ACCUEIL]
  const precedente =
    bloc && typeof bloc === 'object' && typeof (bloc as Record<string, unknown>)['derniereVisite'] === 'string'
      ? ((bloc as Record<string, unknown>)['derniereVisite'] as string)
      : null
  const retour = avecRetour ? noterRetour(existant?.brut[CLE_RETOUR_ABSENCE], precedente, Date.parse(maintenant)) : null
  if (!ecrire) return { precedente, retour }

  const accueil = { derniereVisite: maintenant }
  if (existant) {
    // Un bloc retourAbsence périmé (ou celui d'un fournisseur) s'efface.
    const reste = { ...existant.brut }
    delete reste[CLE_RETOUR_ABSENCE]
    await graphPatch<unknown>(`${listPath()}/items/${existant.itemId}/fields`, {
      Prefs: JSON.stringify({ ...reste, [CLE_ACCUEIL]: accueil, ...(retour ? { [CLE_RETOUR_ABSENCE]: retour } : {}) }),
    })
  } else {
    await graphPost<unknown>(`${listPath()}/items`, {
      fields: { Title: upn, Prefs: JSON.stringify({ [CLE_ACCUEIL]: accueil }) },
    })
  }
  return { precedente, retour }
}

/**
 * Change le bloc retourAbsence (étape cochée, récap vu), relu juste avant :
 * un autre appareil a pu cocher entre-temps. Rien n'est écrit s'il n'y a plus
 * de bloc (périmé, effacé) ; renvoie le bloc écrit.
 */
export async function modifierRetourAbsence(
  upn: string,
  changer: (bloc: BlocRetourAbsence) => BlocRetourAbsence,
): Promise<BlocRetourAbsence | null> {
  if (!upn) return null
  const existant = await findItemByUpn(upn)
  const bloc = lireBlocRetour(existant?.brut[CLE_RETOUR_ABSENCE])
  if (!existant || !bloc) return null
  const nouveau = changer(bloc)
  await graphPatch<unknown>(`${listPath()}/items/${existant.itemId}/fields`, {
    Prefs: JSON.stringify({ ...existant.brut, [CLE_RETOUR_ABSENCE]: nouveau }),
  })
  return nouveau
}

// ─── Helpers ───

/**
 * Calcule les prefs par défaut selon le rôle de l'utilisateur.
 *
 * - coordinateur  → ses métiers d'abord (« Mes métiers (coord) » coché)
 * - les autres    → tout
 */
export function buildDefaultPrefs(
  role: 'operateur' | 'coordinateur' | 'fournisseur',
): DashboardPrefs {
  if (role === 'coordinateur') {
    return {
      ...PREFS_DEFAUT,
      mesMetiersCoordUniquement: true,
    }
  }
  // Opérateur et fournisseur : tout, par défaut. Un fournisseur ne reçoit de
  // toute façon que ses propres déclarations (filtre forcé dans useIssues).
  return { ...PREFS_DEFAUT }
}

// ─── Présence : qui est connecté ───
//
// Pas de serveur temps réel (Static Web App) : chaque appli ouverte écrit
// toutes les minutes « je suis là, sur telle page » dans SA ligne de
// SCE_UserPrefs, et relit la liste. Est « présent » qui a signalé son
// activité depuis moins de FENETRE_PRESENCE_MS.
//
// Colonnes à part de `Prefs` : écrire le JSON à chaque battement risquerait
// d'écraser une préférence modifiée entre-temps sur un autre onglet.

export const FENETRE_PRESENCE_MS = 3 * 60_000

export interface Present {
  upn: string
  /** Nom affiché, lu dans « Créé par » de la ligne — créée par l'intéressé. */
  nom: string
  /** Chemin de la page ouverte, ex. `/issues/P2026-0042`. */
  page: string
  derniereActivite: string
}

// L'identifiant de SA ligne, pour ne pas la rechercher à chaque minute.
let lignePresence: { upn: string; itemId: string } | null = null

export async function signalerPresence(upn: string, page: string): Promise<void> {
  if (!upn) return
  const champs = { [COL_ACTIVITE]: new Date().toISOString(), [COL_PAGE]: page }
  if (lignePresence?.upn !== upn) {
    const existante = await findItemByUpn(upn)
    if (!existante) {
      const creee = await graphPost<{ id: string }>(`${listPath()}/items`, {
        fields: { Title: upn, ...champs },
      })
      lignePresence = { upn, itemId: creee.id }
      return
    }
    lignePresence = { upn, itemId: existante.itemId }
  }
  await graphPatch<unknown>(`${listPath()}/items/${lignePresence.itemId}/fields`, champs)
}

/**
 * « Apparaître non connecté » (26/10) : efface le dernier battement pour
 * disparaître tout de suite des listes des autres, sans attendre la fin de la
 * fenêtre de présence. Rien à faire si la ligne n'existe pas encore.
 * Une date ancienne plutôt que null : elle passe à coup sûr dans la colonne
 * date/heure, et listerPresents l'écarte comme n'importe quel vieux battement.
 */
export const BATTEMENT_EFFACE = '2000-01-01T00:00:00Z'

export async function effacerPresence(upn: string): Promise<void> {
  if (!upn) return
  if (lignePresence?.upn !== upn) {
    const existante = await findItemByUpn(upn)
    if (!existante) return
    lignePresence = { upn, itemId: existante.itemId }
  }
  await graphPatch<unknown>(`${listPath()}/items/${lignePresence.itemId}/fields`, {
    [COL_ACTIVITE]: BATTEMENT_EFFACE,
    [COL_PAGE]: '',
  })
}

interface PresenceFields {
  Title?: string
  [COL_ACTIVITE]?: string
  [COL_PAGE]?: string
}

interface ItemPresence {
  id: string
  createdBy?: { user?: { displayName?: string } }
  fields: PresenceFields
}

/**
 * Les personnes actives dans la fenêtre de présence. Toute la liste est lue
 * (une ligne par utilisateur, quelques dizaines) et filtrée ici : la colonne
 * de date n'est pas indexée.
 */
export async function listerPresents(maintenant: number = Date.now()): Promise<Present[]> {
  const chemin =
    `${listPath()}/items?$expand=fields($select=Title,${COL_ACTIVITE},${COL_PAGE})` +
    `&$select=id,createdBy&$top=500`
  const reponse = await graphGet<{ value: ItemPresence[] }>(chemin)
  const seuil = maintenant - FENETRE_PRESENCE_MS
  return reponse.value.flatMap((item) => {
    const f = item.fields
    const quand = f[COL_ACTIVITE]
    const t = quand ? Date.parse(quand) : Number.NaN
    // Une ligne sans « @ » n'est pas un utilisateur : `__global__` porte les
    // couleurs métier (Lot 8, couleursMetier.api.ts).
    if (!f.Title?.includes('@') || !quand || Number.isNaN(t) || t < seuil) return []
    return [{
      upn: f.Title,
      // Ligne créée depuis la tablette ou le téléphone d'un salarié : « Créé
      // par » est cet appareil, le nom vient de la table (comptesAlias.config).
      nom: nomComplet(nomDansLaTable(f.Title) ?? (item.createdBy?.user?.displayName || f.Title), f.Title),
      page: f[COL_PAGE] ?? '',
      derniereActivite: quand,
    }]
  })
}

// ─── Colonnes lues par Power Automate (Lots 10 bis, 12 bis et 12 ter) ───
//
// Colonnes à part de `Prefs`, comme la présence : les flows les lisent
// directement, sans décoder de JSON. Une ligne de texte chacune, créées sans
// espace ni accent (nom interne = nom d'affichage).

const COL_CANAUX = 'CanauxNotification'     // créée le 25/09 — utils/canauxNotification.ts
const COL_PROJETS = 'ProjetsSuivis'         // Lot 12 bis — utils/elementsSuivis.ts
const COL_EQUIPEMENTS = 'EquipementsSuivis' // Lot 12 ter — utils/elementsSuivis.ts

type ColonneFlux = typeof COL_CANAUX | typeof COL_PROJETS | typeof COL_EQUIPEMENTS

const COLONNE_DU_GENRE: Record<GenreSuivi, ColonneFlux> = {
  projet: COL_PROJETS,
  equipement: COL_EQUIPEMENTS,
}

/** SA ligne et la valeur des colonnes demandées ; null si la ligne n'existe pas. */
async function lireColonnes(
  upn: string,
  colonnes: readonly ColonneFlux[],
): Promise<{ itemId: string; valeurs: Partial<Record<ColonneFlux, string>> } | null> {
  const filter = `fields/Title eq '${escapeODataString(upn)}'`
  const path =
    `${listPath()}/items?$expand=fields($select=Title,${colonnes.join(',')})` +
    `&$filter=${encodeURIComponent(filter)}&$top=1`
  const response = await graphGet<SharePointListResponse<Record<string, string | undefined>>>(path)
  const item = response.value[0]
  if (!item) return null
  const valeurs: Partial<Record<ColonneFlux, string>> = {}
  for (const c of colonnes) {
    const v = item.fields[c]
    if (typeof v === 'string') valeurs[c] = v
  }
  return { itemId: item.id, valeurs }
}

/** SA ligne et la valeur d'une de ces colonnes ; null si la ligne n'existe pas. */
async function lireColonne(
  upn: string,
  colonne: ColonneFlux,
): Promise<{ itemId: string; valeur: string | null } | null> {
  const ligne = await lireColonnes(upn, [colonne])
  return ligne ? { itemId: ligne.itemId, valeur: ligne.valeurs[colonne] ?? null } : null
}

/** Écrit la seule colonne visée, dans la ligne donnée ou dans une ligne créée. */
async function ecrireColonne(
  upn: string,
  colonne: ColonneFlux,
  valeur: string,
  itemId: string | undefined,
): Promise<void> {
  if (itemId) {
    await graphPatch<unknown>(`${listPath()}/items/${itemId}/fields`, { [colonne]: valeur })
    return
  }
  await graphPost<unknown>(`${listPath()}/items`, { fields: { Title: upn, [colonne]: valeur } })
}

// ─── Canaux de notification (Lot 10 bis) ───
//
// `Mail,Teams`, `Mail`, `Teams` ou `Aucun` ; vide = jamais réglé = les deux.

/** Valeur brute de la colonne ; null si la personne n'a jamais rien réglé. */
export async function lireCanauxNotification(upn: string): Promise<string | null> {
  if (!upn) return null
  const ligne = await lireColonne(upn, COL_CANAUX)
  return ligne?.valeur ?? null
}

/** Écrit la valeur (déjà formée par `ecrireCanaux`) dans SA ligne, créée au besoin. */
export async function enregistrerCanauxNotification(upn: string, valeur: string): Promise<void> {
  if (!upn) throw new GraphApiError('UPN manquant pour les canaux de notification', 400)
  const ligne = await lireColonne(upn, COL_CANAUX)
  await ecrireColonne(upn, COL_CANAUX, valeur, ligne?.itemId)
}

/**
 * Canal et FNC-T suivies de TOUT le monde, par adresse en minuscules (lot F6,
 * notifications FNC-T) : une lecture de la liste entière, une ligne par
 * utilisateur (quelques dizaines), comme la présence.
 */
export async function lireReglagesNotification(): Promise<Map<string, ReglagesNotification>> {
  const reglages = new Map<string, ReglagesNotification>()
  let suivante: string | null = `${listPath()}/items?$expand=fields($select=Title,Prefs,${COL_CANAUX})&$select=id&$top=500`
  while (suivante) {
    const page: SharePointListResponse<Record<string, string | undefined>> & { '@odata.nextLink'?: string } =
      await graphGet(suivante)
    for (const item of page.value) {
      const upn = item.fields.Title ?? ''
      // `__global__` et toute ligne sans « @ » ne sont pas des personnes.
      if (!upn.includes('@')) continue
      reglages.set(upn.trim().toLowerCase(), reglagesDepuisLigne(item.fields[COL_CANAUX], item.fields.Prefs))
    }
    suivante = page['@odata.nextLink'] ?? null
  }
  return reglages
}

// ─── Projets et équipements suivis (Lots 12 bis et 12 ter) ───
//
// `;PN4334;PN3394;` et `;4825-SC;2601-H;` — le flow 1 retrouve les abonnés
// par `substringof(';PN4334;', ProjetsSuivis)` et
// `substringof(';4825-SC;', EquipementsSuivis)`.

/**
 * Ce qu'il faut savoir d'une personne mentionnée (« @ », 02/11) : son canal et
 * ce qu'elle suit — un projet ou un équipement suivi lui vaut déjà l'alerte du
 * flow 1 à la déclaration. Pas de ligne = jamais réglé.
 */
export async function lireReglagesMentionne(upn: string): Promise<ReglagesMentionne> {
  const ligne = await lireColonnes(upn, [COL_CANAUX, COL_PROJETS, COL_EQUIPEMENTS])
  const canaux = ligne?.valeurs[COL_CANAUX]?.trim()
  return {
    canaux: canaux ? canaux : null,
    projets: lireSuivis(ligne?.valeurs[COL_PROJETS]),
    equipements: lireSuivis(ligne?.valeurs[COL_EQUIPEMENTS]),
  }
}

/** Les deux listes en une seule lecture (l'accueil a besoin des deux). */
export async function lireElementsSuivis(upn: string): Promise<ElementsSuivis> {
  if (!upn) return { projets: [], equipements: [] }
  const ligne = await lireColonnes(upn, [COL_PROJETS, COL_EQUIPEMENTS])
  return {
    projets: lireSuivis(ligne?.valeurs[COL_PROJETS]),
    equipements: lireSuivis(ligne?.valeurs[COL_EQUIPEMENTS]),
  }
}

const LIBELLES_TROP: Record<GenreSuivi, string> = {
  projet: 'Trop de projets suivis',
  equipement: 'Trop d’équipements suivis',
}

/**
 * Suivre / ne plus suivre un PN ou un N° de série. Relecture juste avant
 * l'écriture : la colonne porte TOUS les éléments de la personne, un autre
 * onglet a pu en ajouter un entre-temps.
 *
 * @returns la liste telle qu'enregistrée.
 */
export async function changerSuivi(
  upn: string,
  genre: GenreSuivi,
  id: string,
  suivre: boolean,
): Promise<string[]> {
  if (!upn) throw new GraphApiError('UPN manquant pour suivre un élément', 400)
  const colonne = COLONNE_DU_GENRE[genre]
  const ligne = await lireColonne(upn, colonne)
  const actuels = lireSuivis(ligne?.valeur)
  if (suit(actuels, id) === suivre) return actuels
  const apres = suivisApres(actuels, id, suivre)
  const valeur = ecrireSuivis(apres)
  if (valeur.length > LONGUEUR_MAX_SUIVIS) {
    throw new GraphApiError(
      `${LIBELLES_TROP[genre]} (${actuels.length}) : arrêtez d'en suivre un avant d'en ajouter.`,
      400,
    )
  }
  await ecrireColonne(upn, colonne, valeur, ligne?.itemId)
  return apres
}
